import {useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useTranslation} from "react-i18next";
import {useNavigate, useParams} from "react-router-dom";
import {toast} from "react-toastify";
import {GetEmployeeDetailsByUserId, GetServiceById} from "../../../../reduxToolkit/Employee/EmployeeApi";
import {createImgObj, userInfoStatus} from "../../../../reduxToolkit/CompanyEmployees/CompanyEmployeesApi";
import {UploadImage} from "../../../../reduxToolkit/ShareSlice/shareApi";
import {
    AssignGuardZoneAccess,
    DeleteGuardDocument,
    GetGuardDocuments,
    GetZoneAccessList,
} from "../../../../reduxToolkit/Guards/GuardsApi";
import {FINGERPRINT_SOURCE_MODEL, isStrongPin, PIN_LENGTH, SERVICE} from "../../Company/Employees/Model/Models";
import {GUARD_CONTRACT_DATA, GUARD_USER_DATA, toGuardDocument} from "../Model/Model";
import {
    errorMsg,
    isEmailFormat,
    isPhoneFormat,
    isValidLengthDocumentId,
    postSessionStorageLoader,
    validateLengthRange,
    validateMinLengthTrimmed
} from "../../../../Apis/functions";
import {PHOTO_PICKER_MODEL} from "../../../../components/PhotoPicker/SubComponent/Models/Models";
import defaultImage from "../../../../assets/ImagesDefaults/ic-person.svg";
import {accessMethodModel} from "../../../../enums/accessMethodEnum";
import {TABLES} from "../../../../Apis/Tables";
import {PASSWORD_MIN_LENGTH} from "../../../../constant/variable";

export const FINGER_NAME = 'fingerprint.wsq';

const isFilled = (value) => value !== '' && value != null;

/**
 * Common logic of the creation and the update of a guard: its details, its contract, its photo,
 * its fingerprints, its zone accesses and its documents, saved through the guard endpoints. It reuses
 * the steps of the employee enrollment, but only reads the guard, not the catalogs, vehicles or
 * devices of an employee.
 */
const CreateUpdateGuardCommon = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const {t} = useTranslation();
    const {id} = useParams();
    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    const {permission} = useSelector((state) => state.authenticatioauthennSlice);
    const disabled = false;

    const [activeStep, setActiveStep] = useState(0);
    const [emptyFields, setEmptyFields] = useState(false);
    const [emptySelfie, setEmptySelfie] = useState(false);
    const [isValidEmail, setIsValidEmail] = useState(true);
    const [isValidPassword, setIsValidPassword] = useState(true);
    const [isValidDocumentId, setIsValidDocumentId] = useState(true);
    const [isValidPhoneNumber, setIsValidPhoneNumber] = useState(true);
    const [isValidDob, setIsValidDob] = useState(true);
    const [isValidEndDate, setIsValidEndDate] = useState(true);
    const [isValidPin, setIsValidPin] = useState(true);
    const [isPilgrimsValidation, setIsPilgrimsValidation] = useState(false);

    const [userData, setUserData] = useState({...GUARD_USER_DATA});
    const [employeeData, setEmployeeData] = useState({...GUARD_CONTRACT_DATA});
    const [photoPicker, setPhotoPicker] = useState({...PHOTO_PICKER_MODEL, preview: defaultImage});
    // Selfie the guard has saved, it is deleted when the photo is removed from the picker
    const [savedSelfieId, setSavedSelfieId] = useState(null);

    const [selectedReader, setSelectedReader] = useState('');
    const [availableReaders, setAvailableReaders] = useState([]);
    const [fingerprintName, setFingerprintName] = useState('');
    const [capturedFingerprint, setCapturedFingerprint] = useState(null);
    const [fingerprintSrc, setFingerprintSrc] = useState('');
    const [fingerprintsList, setFingerprintsList] = useState([]);
    // Fingerprints captured before the guard exists, they are uploaded once it is created
    const [fingerprintsTemp, setFingerprintsTemp] = useState([]);
    const [capturing, setCapturing] = useState(false);
    const [updated, setUpdated] = useState(false);
    const [updatedFinger, setUpdatedFinger] = useState(false);
    const [modified, setModified] = useState(false);
    const [loadingProgress, setLoadingProgress] = useState(false);

    const isUpdate = !!id;
    const [zoneAccesses, setZoneAccesses] = useState([]);
    const [zoneAccessIds, setZoneAccessIds] = useState([]);
    const [documents, setDocuments] = useState([]);
    const [secretModal, setSecretModal] = useState(false);
    const [secretKey, setSecretKey] = useState(null);

    useEffect(() => {
        dispatch(GetServiceById(SERVICE.PILGRIMS)).then(({payload: {data: {data, success}}}) => {
            if (success) setIsPilgrimsValidation(data?.validation);
        });
        // Options of the status field of the details, as the employee reads them
        dispatch(userInfoStatus());
        dispatch(GetZoneAccessList()).then(({payload: {data: {data, success}}}) => {
            if (success) setZoneAccesses(data || []);
        });
    }, []);

    useEffect(() => {
        if (!loadingProgress) return;
        const timer = setTimeout(() => setLoadingProgress(false), 2000);
        return () => clearTimeout(timer);
    }, [loadingProgress]);

    // The fingerprint reader reads whether a capture is running from the session storage
    useEffect(() => {
        sessionStorage.setItem('capturing', capturing);
        window.postMessage({type: 'sessionStorageChange', newValue: capturing}, '*');
    }, [capturing]);

    /**
     * Reads the guard of the profile: its details, its contract, its selfie and its fingerprints.
     */
    const getData = () => {
        setUpdatedFinger(false);
        if (!id) return;
        dispatch(GetEmployeeDetailsByUserId(id)).then(({payload: {data: {data}}}) => {
            setEmployeeData((prev) => ({...prev, ...data?.employee}));
            setUserData((prev) => ({...prev, ...data?.user}));
            setFingerprintsList(data?.fingerprints);
            setSavedSelfieId(data?.selfie?.id || null);
            if (data?.selfie) {
                const image = `data:image/jpeg;base64,${data.selfie.image}`;
                setPhotoPicker((prev) => ({...prev, previewImage: image, id: data.selfie.id}));
            } else {
                setPhotoPicker({...PHOTO_PICKER_MODEL, preview: defaultImage, id: null});
            }
            setUpdated(false);
            setLoadingProgress(false);
            postSessionStorageLoader(false);
        });
    };

    useEffect(() => {
        getData();
    }, [id]);

    // A change flagged by a step reloads the guard once and clears the flag
    useEffect(() => {
        if (!modified) return;
        getData();
        setModified(false);
    }, [modified]);

    useEffect(() => {
        if (!updated) return;
        getData();
        setUpdated(false);
    }, [updated]);

    useEffect(() => {
        setIsValidEmail(isFilled(userData.email) ? isEmailFormat(userData.email) : true);
    }, [userData.email]);

    useEffect(() => {
        setIsValidPhoneNumber(isFilled(userData.phoneNumber) ? isPhoneFormat(userData.phoneNumber) : true);
    }, [userData.phoneNumber]);

    useEffect(() => {
        setIsValidDocumentId(isFilled(userData.documentId) ? isValidLengthDocumentId(userData.documentId) : true);
    }, [userData.documentId]);

    useEffect(() => {
        setIsValidPin(isFilled(userData.pin)
            ? validateLengthRange(userData.pin, PIN_LENGTH.MIN, PIN_LENGTH.MAX) && isStrongPin(userData.pin)
            : true);
    }, [userData.pin]);

    useEffect(() => {
        setIsValidPassword(validateMinLengthTrimmed(userData.password, {minLength: PASSWORD_MIN_LENGTH, allowEmpty: true}));
    }, [userData.password]);

    const isSelfieValid = !!photoPicker?.selfieSrc || !!photoPicker?.srcSelected || !!photoPicker?.previewImage;

    useEffect(() => {
        if (isSelfieValid) setEmptySelfie(false);
    }, [isSelfieValid]);

    const handleFormChangeUserData = (e) => {
        const {name, value} = e.target;
        setUserData((prev) => ({...prev, [name]: value}));
    };

    const handleFormChangeEmployeeData = (e) => {
        const {name, value} = e.target;
        setEmployeeData((prev) => ({...prev, [name]: value}));
    };

    const handleStepChange = (step) => setActiveStep(step);

    const handleClearUserData = () => {
        setUserData({...GUARD_USER_DATA});
        setIsValidEmail(true);
        setIsValidDocumentId(true);
        setIsValidPhoneNumber(true);
        setIsValidDob(true);
        setIsValidPin(true);
        setEmptyFields(false);
    };

    const handleValidationStepDetails = () => {
        const valid = userData.isValid() && isValidEmail && isValidPhoneNumber && isValidDob
            && isValidDocumentId && isValidPin && isValidPassword;
        setEmptyFields(!valid);
        if (valid) setActiveStep((prev) => prev + 1);
    };

    /**
     * A fingerprint is optional, but once a reader is selected it needs a name.
     */
    const handleValidationFinger = () => {
        const valid = selectedReader === '' || fingerprintName !== '';
        setEmptyFields(!valid);
        if (valid) setActiveStep((prev) => prev + 1);
    };

    // The readers are listed by the capture step itself
    const getAvailableReaders = () => {};

    const handleCloseModal = () => {
        setCapturing(false);
        setUpdatedFinger(true);
    };

    // Saved fingerprints and the ones pending, as the fingerprints step of the employee lists them
    const mergedFingerprints = [
        ...(fingerprintsList || []).map((item) => ({...item, source: FINGERPRINT_SOURCE_MODEL.SERVER})),
        ...fingerprintsTemp.map((item) => ({...item, source: FINGERPRINT_SOURCE_MODEL.TEMP})),
    ];

    /**
     * Saves the fingerprint captured in the profile of the guard. Before the guard exists, it is
     * kept as pending and uploaded once the guard is created.
     */
    const handleUploadFinger = () => {
        if (!capturedFingerprint) {
            setEmptyFields(true);
            return;
        }
        setEmptyFields(false);
        if (!userData?.id) {
            setFingerprintsTemp((prev) => [
                ...prev,
                {id: Date.now(), name: fingerprintName, file: capturedFingerprint},
            ]);
            setCapturedFingerprint(null);
            setFingerprintName('');
            setFingerprintSrc('');
            setSelectedReader('');
            return;
        }
        dispatch(createImgObj({
            accessMethod: {id: accessMethodModel.FINGER_PRINT.id},
            user: {id: userData.id},
            description: fingerprintName,
        })).then(({payload: {data: {data, success}}}) => {
            if (!success) return;
            const formData = new FormData();
            formData.append('id', data.id);
            formData.append('option', TABLES.USER);
            formData.append('file', capturedFingerprint, FINGER_NAME);
            dispatch(UploadImage(formData)).then(({payload: {data: {success, code}}}) => {
                if (!success) {
                    errorMsg(code);
                    return;
                }
                setUpdated(true);
                setUpdatedFinger(true);
                setSelectedReader('');
                setAvailableReaders([]);
                setFingerprintSrc('');
                setCapturedFingerprint(null);
                setFingerprintName('');
                toast.success(t('has_been_created_successfully'));
            });
        });
    };

    const getDocuments = (userId) => {
        dispatch(GetGuardDocuments(userId)).then(({payload: {data: {data, success}}}) => {
            if (success) setDocuments((data || []).map(toGuardDocument));
        });
    };

    /**
     * The documents are kept until the guard is created or updated, the ones pending carry their file.
     */
    const addDocument = (name, file) => {
        setDocuments((prev) => [...prev, {id: `${Date.now()}`, name, fileName: file.name, file}]);
    };

    /**
     * A pending document is only dropped from the list, a saved one is deleted with its file.
     */
    const removeDocument = (document) => {
        if (document.file) {
            setDocuments((prev) => prev.filter((item) => item.id !== document.id));
            return;
        }
        dispatch(DeleteGuardDocument(document.id)).then(({payload: {data: {success, code}}}) => {
            if (success) {
                toast.success(t('has_been_deleted_successfully'));
                getDocuments(userData.id);
            } else errorMsg(code);
        });
    };

    const assignZoneAccess = (userId) => dispatch(AssignGuardZoneAccess({
        userId,
        zoneAccessIds,
    }));

    /**
     * Uploads an image of the user with the access method it is used for.
     */
    const uploadUserImage = (userId, accessMethodId, description, file, fileName) =>
        dispatch(createImgObj({accessMethod: {id: accessMethodId}, user: {id: userId}, description}))
            .then(({payload: {data: {data, success}}}) => {
                if (!success) return;
                const formData = new FormData();
                formData.append('id', data.id);
                formData.append('option', TABLES.USER);
                if (fileName) formData.append('file', file, fileName);
                else formData.append('file', file);
                return dispatch(UploadImage(formData));
            });

    return {
        t,
        dispatch,
        navigate,
        DEV,
        permission,
        disabled,
        activeStep,
        setActiveStep,
        handleStepChange,
        emptyFields,
        setEmptyFields,
        emptySelfie,
        setEmptySelfie,
        isValidEmail,
        isValidPassword,
        isValidDocumentId,
        isValidPhoneNumber,
        isValidDob,
        setIsValidDob,
        isValidEndDate,
        setIsValidEndDate,
        isValidPin,
        isPilgrimsValidation,
        userData,
        setUserData,
        handleFormChangeUserData,
        handleClearUserData,
        handleValidationStepDetails,
        employeeData,
        setEmployeeData,
        handleFormChangeEmployeeData,
        photoPickerFile: photoPicker,
        setPhotoPickerFile: setPhotoPicker,
        savedSelfieId,
        isSelfieValid,
        selectedReader,
        setSelectedReader,
        availableReaders,
        setAvailableReaders,
        getAvailableReaders,
        fingerprintName,
        setFingerprintName,
        capturedFingerprint,
        setCapturedFingerprint,
        fingerprintSrc,
        setFingerprintSrc,
        fingerprintsList,
        fingerprintsTemp,
        setFingerprintsTemp,
        mergedFingerprints,
        handleUploadFinger,
        handleValidationFinger,
        capturing,
        setCapturing,
        handleCloseModal,
        updated,
        setUpdated,
        updatedFinger,
        setUpdatedFinger,
        setModified,
        loadingProgress,
        setLoadingProgress,
        isUpdate,
        zoneAccesses,
        zoneAccessIds,
        setZoneAccessIds,
        documents,
        setDocuments,
        getDocuments,
        addDocument,
        removeDocument,
        assignZoneAccess,
        uploadUserImage,
        secretModal,
        setSecretModal,
        secretKey,
        setSecretKey,
    };
};

export default CreateUpdateGuardCommon;
